import { useMemo, useState } from "react";
import { ApiError, fetchFileBlobUrl } from "@/api/client";
import { useAdminPersonDocuments, useAdminUploadDocument, useAdminUsers } from "@/api/queries";
import type { AdminUser, DocType } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { CategoryBarChart, StatTile } from "@/components/charts";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input, Select } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, ErrorState, SkeletonTable, SkeletonText } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { formatTimestampDate } from "@/lib/format";
import { ALL_DOC_TYPES, DOC_TYPE_LABEL } from "@/lib/labels";

/**
 * HR has full read/write on every non-admin account's documents and personal
 * profile, including Offer Letter and Compensation Sheet, which only this route
 * can upload.
 */
export function HrDocumentsPage() {
  const users = useAdminUsers();
  const [query, setQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [openPerson, setOpenPerson] = useState<AdminUser | null>(null);

  // HR & Admin are employees of the company too, so they belong in headcount,
  // analytics and the directory alongside everyone else.
  const staff = users.data ?? [];
  const teams = useMemo(
    () => [...new Set(staff.map((u) => u.team_name ?? "Unassigned"))].sort(),
    [staff],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return staff.filter((person) => {
      const matchesTeam = teamFilter === "all" || (person.team_name ?? "Unassigned") === teamFilter;
      if (!matchesTeam) return false;
      if (!needle) return true;
      return [person.name, person.email].filter(Boolean).some((f) => String(f).toLowerCase().includes(needle));
    });
  }, [staff, query, teamFilter]);

  const byTeam = useMemo(() => {
    const map = new Map<string, { team: string; people: number }>();
    for (const person of staff) {
      const team = person.team_name ?? "Unassigned";
      const entry = map.get(team) ?? { team, people: 0 };
      entry.people += 1;
      map.set(team, entry);
    }
    return [...map.values()].sort((a, b) => b.people - a.people);
  }, [staff]);

  if (users.error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Org-wide" title="Documents & Compliance" />
        <Panel>
          <ErrorState message={users.error.message} onRetry={() => void users.refetch()} />
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Org-wide"
        title="Documents & Compliance"
        sub="Personal details and documents for every account, organised by team, plus simplified PF compliance. HR and Admin sees everything here."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Records on file" value={staff.length} icon="documents" loading={users.isLoading} />
        <StatTile
          label="Teams covered"
          value={byTeam.length}
          icon="org"
          loading={users.isLoading}
        />
        <StatTile
          label="Document types tracked"
          value={ALL_DOC_TYPES.length}
          icon="document"
          meta="Aadhaar, PAN, cheque, offer, compensation"
        />
        {/*
          Headcount, labelled as headcount. This tile used to say "Enrolled in PF"
          over the same figure, asserting a compliance fact from a proxy — while
          the panel further down this very page states that the system holds no
          PF contribution amounts, UAN numbers or challan status. There is no PF
          field anywhere in the API; until there is, this cannot report on it.
        */}
        <StatTile
          label="Accounts on file"
          value={staff.length}
          icon="verified"
          loading={users.isLoading}
          meta="PF enrolment is not tracked in this system"
        />
      </div>

      <CategoryBarChart
        title="Records per team"
        note="Each account carries one personal record and up to five documents"
        data={byTeam.map((entry) => ({ label: entry.team, value: entry.people }))}
        valueHeader="Records"
        loading={users.isLoading}
        emptyBody="No accounts have been created yet."
      />

      <Panel>
        <PanelHeader
          title="Employee records"
          note={`${filtered.length} of ${staff.length} shown · open a record to view details and documents`}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Icon
                  name="search"
                  size={16}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-faint"
                />
                <Input
                  aria-label="Search records"
                  placeholder="Search name or email"
                  className="h-8 w-52 pl-8 text-[13px]"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <Select
                aria-label="Filter by team"
                className="h-8 w-auto text-[13px]"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
              >
                <option value="all">All teams</option>
                {teams.map((team) => (
                  <option key={team} value={team}>
                    {team}
                  </option>
                ))}
              </Select>
            </div>
          }
        />
        {users.isLoading ? (
          <SkeletonTable rows={8} cols={4} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="documents"
            title={staff.length === 0 ? "No records yet" : "Nothing matches that"}
            body={
              staff.length === 0
                ? "Records are created automatically when HR creates an account and that person completes their setup."
                : "Try a different search or team."
            }
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Team</TH>
              <TH>Designation</TH>
              <TH>
                <span className="sr-only">Open</span>
              </TH>
            </THead>
            <TBody>
              {filtered.map((person) => (
                <TR key={person.id}>
                  <TDPrimary>
                    <span className="flex items-center gap-2">
                      <Avatar name={person.name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate">{person.name ?? "Unnamed"}</span>
                        <span className="block truncate font-mono text-xs font-normal text-muted">
                          {person.email}
                        </span>
                      </span>
                    </span>
                  </TDPrimary>
                  <TD>{person.team_name ?? "Unassigned"}</TD>
                  <TD>{person.designation}</TD>
                  <TD>
                    <Button variant="ghost" size="sm" onClick={() => setOpenPerson(person)}>
                      Open record
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      <Panel>
        <PanelHeader
          title="PF compliance"
          note="Simplified: Mentoria runs Provident Fund only, with no ESI, PT or TDS handling in this system"
        />
        <div className="px-4 py-4">
          <p className="max-w-[65ch] text-sm leading-relaxed text-muted">
            The system records who is on payroll but does not hold PF contribution amounts, UAN
            numbers or monthly challan status. Those live with the payroll provider, so this screen
            reports enrolment only rather than showing a processing status it cannot verify.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-line p-3">
              <p className="text-xs text-muted">Accounts on the system</p>
              <p className="mt-1 text-2xl font-semibold tracking-tight text-ink">{staff.length}</p>
            </div>
            <div className="rounded-lg border border-line p-3">
              <p className="text-xs text-muted">Contribution data held here</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-ink">
                <Icon name="info" size={16} className="text-faint" />
                None, by design
              </p>
            </div>
          </div>
        </div>
      </Panel>

      {openPerson && <PersonRecordDialog person={openPerson} onClose={() => setOpenPerson(null)} />}
    </div>
  );
}

function PersonRecordDialog({ person, onClose }: { person: AdminUser; onClose: () => void }) {
  const record = useAdminPersonDocuments(person.id);
  const upload = useAdminUploadDocument();
  const [busyType, setBusyType] = useState<DocType | null>(null);

  const profile = record.data?.profile;
  const documents = record.data?.documents ?? [];
  const onFile = new Set(documents.map((d) => d.doc_type));

  async function openDocument(docType: DocType) {
    try {
      const url = await fetchFileBlobUrl(`/api/admin/documents/${person.id}/file/${docType}`);
      window.open(url, "_blank", "noopener,noreferrer");
      // The blob stays alive long enough for the new tab to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not open that document.");
    }
  }

  const detailRows: { label: string; value: string | null }[] = [
    { label: "Aadhaar number", value: profile?.aadhaar_number ?? null },
    { label: "PAN number", value: profile?.pan_number ?? null },
    { label: "Gender", value: profile?.gender ?? null },
    { label: "Blood group", value: profile?.blood_group ?? null },
    { label: "Account holder", value: profile?.account_holder_name ?? null },
    { label: "Account number", value: profile?.account_number ?? null },
    { label: "Bank", value: profile?.bank_name ?? null },
    { label: "IFSC", value: profile?.ifsc_code ?? null },
    { label: "Current address", value: profile?.current_address ?? null },
    { label: "Permanent address", value: profile?.permanent_address ?? null },
    { label: "Emergency contact 1", value: joinContact(profile?.emergency_contact_1_name, profile?.emergency_contact_1_number) },
    { label: "Emergency contact 2", value: joinContact(profile?.emergency_contact_2_name, profile?.emergency_contact_2_number) },
  ];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent wide>
        <DialogHeader
          title={person.name ?? person.email}
          description={`${person.designation} · ${person.team_name ?? "Unassigned"}`}
        />
        <DialogBody className="flex flex-col gap-5">
          {record.isLoading ? (
            <SkeletonText lines={8} />
          ) : record.error ? (
            <ErrorState message={record.error.message} onRetry={() => void record.refetch()} />
          ) : (
            <>
              <section>
                <p className="eyebrow mb-2">Personal details</p>
                <dl className="divide-y divide-line rounded-lg border border-line">
                  {detailRows.map((row) => (
                    <div key={row.label} className="flex items-start justify-between gap-4 px-3 py-2">
                      <dt className="text-[13px] text-muted">{row.label}</dt>
                      <dd
                        className={
                          row.value
                            ? "max-w-[60%] text-right text-[13px] font-medium text-ink"
                            : "text-right text-[13px] text-faint"
                        }
                      >
                        {row.value ?? "Not provided yet"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section>
                <p className="eyebrow mb-2">Documents</p>
                <ul className="flex flex-col gap-2">
                  {ALL_DOC_TYPES.map((docType) => {
                    const doc = documents.find((d) => d.doc_type === docType);
                    return (
                      <li
                        key={docType}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line p-3"
                      >
                        <div className="flex min-w-0 items-center gap-2.5">
                          <Icon
                            name={onFile.has(docType) ? "check-circle" : "document"}
                            size={20}
                            className={onFile.has(docType) ? "text-good" : "text-faint"}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-ink">{DOC_TYPE_LABEL[docType]}</p>
                            <p className="truncate text-xs text-muted">
                              {doc
                                ? `${doc.original_name} · uploaded ${formatTimestampDate(doc.uploaded_at)}`
                                : "Not on file"}
                            </p>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {doc && (
                            <Button variant="ghost" size="sm" iconLeft="view" onClick={() => void openDocument(docType)}>
                              View
                            </Button>
                          )}
                          <label>
                            <span
                              className={
                                "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-line-strong px-3 text-[13px] font-medium transition-colors hover:bg-surface-sunken active:scale-[0.98]" +
                                (busyType === docType ? " pointer-events-none opacity-50" : "")
                              }
                            >
                              <Icon
                                name={busyType === docType ? "spinner" : "upload"}
                                size={16}
                                className={busyType === docType ? "animate-spin" : ""}
                              />
                              {doc ? "Replace" : "Upload"}
                            </span>
                            <input
                              type="file"
                              className="sr-only"
                              accept="image/*,application/pdf"
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                event.target.value = "";
                                if (!file) return;
                                setBusyType(docType);
                                upload.mutate(
                                  { userId: person.id, docType, file },
                                  {
                                    onSuccess: () =>
                                      toast.success(`${DOC_TYPE_LABEL[docType]} uploaded.`),
                                    onError: (err) =>
                                      toast.error(
                                        err instanceof ApiError ? err.message : "Upload failed.",
                                      ),
                                    onSettled: () => setBusyType(null),
                                  },
                                );
                              }}
                            />
                          </label>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-2 text-xs leading-relaxed text-muted">
                  Offer letter and compensation sheet can only be uploaded here, by HR. Employees can
                  upload their own Aadhaar, PAN and cancelled cheque.
                </p>
              </section>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function joinContact(name: string | null | undefined, number: string | null | undefined) {
  if (!name && !number) return null;
  return [name, number].filter(Boolean).join(" · ");
}
